import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Edit,
  Send,
  Download,
  Trash2,
  Eye,
  Mail,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react';

/**
 * Coloured badge for quote status — uses t() for label translation.
 */
const StatusBadge = ({ status }) => {
  const { t } = useTranslation();

  const badges = {
    draft:    { key: 'draft',    color: '#71717a', icon: Edit        },
    sent:     { key: 'sent',     color: '#3b82f6', icon: Mail        },
    accepted: { key: 'accepted', color: '#10b981', icon: CheckCircle },
    rejected: { key: 'rejected', color: '#ef4444', icon: XCircle     },
    expired:  { key: 'expired',  color: '#f59e0b', icon: Clock       },
  };

  const badge = badges[status] || badges.draft;
  const Icon = badge.icon;

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: '600',
      background: `${badge.color}20`,
      color: badge.color,
    }}>
      <Icon size={12} />
      {t(`admin.quotes.statusLabels.${badge.key}`)}
    </span>
  );
};

/**
 * QuoteCard — renders a single quote row in the list.
 *
 * Props:
 *   quote      — quote object from the API
 *   onEdit     — (quote)    => void
 *   onSend     — (quoteId)  => void
 *   onDownload — (quoteId, quoteNumber) => void
 *   onDelete   — (quoteId)  => void
 *   onView     — (quote)    => void   (opens the readonly preview modal)
 */
const QuoteCard = ({ quote, onEdit, onSend, onDownload, onDelete, onView }) => {
  const { t } = useTranslation();

  return (
    <div className="quote-card" data-status={quote.status}>
      <div className="quote-header">
        <div>
          {/* Clicking the quote number/name opens the readonly preview */}
          <h3
            style={{ cursor: 'pointer', color: '#3b82f6' }}
            onClick={() => onView && onView(quote)}
            title={t('admin.quotes.tooltipPreview')}
          >
            {quote.quoteNumber}
          </h3>
          <p className="quote-customer">{quote.customerName}</p>
        </div>
        <StatusBadge status={quote.status} />
      </div>

      <div className="quote-details">
        <div className="quote-detail-item">
          <span className="label">{t('admin.quotes.preview.issued')}</span>
          <span>{new Date(quote.createdAt).toLocaleDateString('it-IT')}</span>
        </div>
        <div className="quote-detail-item">
          <span className="label">{t('admin.quotes.preview.validUntil')}</span>
          <span>{new Date(quote.validUntil).toLocaleDateString('it-IT')}</span>
        </div>
        <div className="quote-detail-item">
          <span className="label">{t('admin.quotes.items')}</span>
          <span>{quote.items.length}</span>
        </div>
        <div className="quote-detail-item">
          <span className="label">{t('admin.quotes.total')}</span>
          <span className="quote-total">€{quote.total.toFixed(2)}</span>
        </div>
      </div>

      <div className="quote-actions">
        {/* View (readonly preview) */}
        <button
          onClick={() => onView && onView(quote)}
          className="action-btn"
          title={t('admin.quotes.tooltipView')}
          aria-label={t('admin.quotes.tooltipView')}
          style={{ color: '#3b82f6' }}
        >
          <Eye size={16} />
        </button>

        {quote.status !== 'accepted' && (
          <button
            onClick={() => onEdit(quote)}
            className="action-btn edit-btn"
            title={t('admin.quotes.tooltipEdit')}
            aria-label={t('admin.quotes.tooltipEdit')}
          >
            <Edit size={16} />
          </button>
        )}

        {(quote.status === 'draft' || quote.status === 'sent') && (
          <button
            onClick={() => onSend(quote._id)}
            className="action-btn send-btn"
            title={t('admin.quotes.tooltipSend')}
            aria-label={t('admin.quotes.tooltipSend')}
          >
            <Send size={16} />
          </button>
        )}

        <button
          onClick={() => onDownload(quote._id, quote.quoteNumber)}
          className="action-btn download-btn"
          title={t('admin.quotes.tooltipDownload')}
          aria-label={t('admin.quotes.tooltipDownload')}
        >
          <Download size={16} />
        </button>

        {quote.status !== 'accepted' && (
          <button
            onClick={() => onDelete(quote._id)}
            className="action-btn delete-btn"
            title={t('admin.quotes.tooltipDelete')}
            aria-label={t('admin.quotes.tooltipDelete')}
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

export default QuoteCard;
